import { useEffect, useState } from 'react';
import { Bell, Clock, Briefcase, MessageSquare, Mail } from 'lucide-react';
import { useNotificationStore } from 'utils/notificationStore';
import { useCurrentUser } from 'app';
import { useActiveShifts } from 'utils/useActiveShifts';
import { useUserRole } from 'utils/useUserRole';
import { useNavigate } from 'react-router-dom';
import { useChatStore } from 'utils/chatStore';
import { isConversationUnreadFor } from 'utils/chatTypes';
import brain from 'brain';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { formatDistanceToNow } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

export interface NotificationBellProps {
  variant?: 'default' | 'minimal';
}

// Helper function to format time from notification metadata
const formatTimeFromMetadata = (timeValue: any): string => {
  if (!timeValue) return '';
  
  // If it's already a string, return it
  if (typeof timeValue === 'string') return timeValue;
  
  // If it's a Firestore Timestamp object
  if (timeValue.seconds !== undefined) {
    const date = new Date(timeValue.seconds * 1000);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }
  
  return String(timeValue);
};

export default function NotificationBell({ variant = 'default' }: NotificationBellProps) {
  const { user, loading: authLoading } = useCurrentUser();
  const { role, loading: roleLoading } = useUserRole({ user, authLoading });
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [staffAppointmentDialogOpen, setStaffAppointmentDialogOpen] = useState(false);
  const [allNotificationsModalOpen, setAllNotificationsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
  
  // Staff-specific notifications (now handled by Firestore notifications)
  const { activeShifts, loading: activeShiftsLoading } = useActiveShifts();
  
  // Chat store for unread messages
  const { conversations } = useChatStore();
  
  const { 
    notifications, 
    unreadCount, 
    isLoading,
    subscribeToNotifications,
    markAsRead,
    markAllAsRead 
  } = useNotificationStore();

  // Function to count unread messages for current user — shared
  // helper from chatTypes so we honour Bug 1 sender guard and Bug 2
  // admin_client team-acknowledge state. See `buildUnreadClearsForRead`
  // in chatService.ts for the team-clear contract.
  const getUnreadMessagesCount = () => {
    if (!user?.uid || !conversations) return 0;
    return conversations.filter(conversation =>
      isConversationUnreadFor(conversation, user.uid)
    ).length;
  };

  useEffect(() => {
    if (!user?.uid) return;
    
    const unsubscribe = subscribeToNotifications(user.uid);
    return () => unsubscribe();
  }, [user?.uid, subscribeToNotifications]);

  // All notifications now come from Firestore - no need for polling

  // Don't render if no user
  if (!user) return null;

  const activeShiftsCount = activeShifts?.length || 0;
  const unreadMessagesCount = getUnreadMessagesCount();
  const totalUnreadCount = unreadCount + activeShiftsCount + unreadMessagesCount;

  const handleNotificationClick = async (notification: any) => {
    // Handle staff-side notifications (live state - no metadata)
    if (notification.source === 'active-shift') {
      navigate('/my-roster');
      setOpen(false);
      return;
    }

    // Handle chat notification (live state - no metadata)
    if (notification.source === 'chat') {
      // Route to appropriate chat page based on user role
      if (role === 'client' || role === 'limited_access') {
        navigate('/clientchat');
      } else {
        navigate('/chat');
      }
      setOpen(false);
      return;
    }

    // Handle Firestore notifications based on metadata
    if (notification.metadata) {
      // Job-board ACCEPTANCE → deep link into the staff diary at the shift
      // (actionUrl carries /staff-diary?date=...&highlight=...)
      const metaType = notification.metadata.type;
      if (metaType === 'assignment_from_job_board' || metaType === 'transport_assignment_from_job_board') {
        if (notification.id) {
          await markAsRead(notification.id);
        }
        let target = '/staff-diary';
        if (notification.actionUrl) {
          try {
            const u = new URL(notification.actionUrl, window.location.origin);
            target = u.pathname + u.search;
          } catch {}
        }
        navigate(target);
        setOpen(false);
        return;
      }

      // Job Board notifications
      if (notification.metadata.appointmentId && notification.title?.includes('Job Board')) {
        // Mark as read
        if (notification.id) {
          await markAsRead(notification.id);
        }
        navigate('/staff-job-board');
        setOpen(false);
        return;
      }

      // Invitation notifications
      if (notification.metadata.invitationId) {
        // Mark as read
        if (notification.id) {
          await markAsRead(notification.id);
        }
        navigate('/staff-job-board?tab=invitations');
        setOpen(false);
        return;
      }
    }

    // News article notifications → open the article via its deep link
    // (actionUrl carries /news?articleId=…).
    if (
      (notification.type === 'news_article_staff' || notification.type === 'news_article_general') &&
      notification.actionUrl
    ) {
      if (notification.id) {
        await markAsRead(notification.id);
      }
      let target = '/news';
      try {
        const u = new URL(notification.actionUrl, window.location.origin);
        target = u.pathname + u.search;
      } catch {}
      navigate(target);
      setOpen(false);
      return;
    }

    // If it's an appointment confirmation notification, open the appropriate dialog
    if (notification.type === 'appointment' && notification.metadata?.appointmentId) {
      setSelectedAppointment({
        id: notification.metadata.appointmentId,
        serviceName: notification.metadata.serviceName,
        clientName: notification.metadata.clientName,
        startTime: notification.metadata.startTime,
        endTime: notification.metadata.endTime,
        date: notification.metadata.date,
        time: notification.metadata.time,
        clients: notification.metadata.clients,
        location: notification.metadata.location,
      });
      setSelectedNotificationId(notification.id);
      
      // Show different dialog based on user role
      if (role === 'staff' || role === 'admin') {
        setStaffAppointmentDialogOpen(true);
      } else {
        setConfirmDialogOpen(true);
      }
      
      setOpen(false);
    }
  };

  const handleConfirm = async (willAttend: boolean) => {
    if (!selectedAppointment?.id) return;

    setIsConfirming(true);
    try {
      const response = await brain.confirm_appointment(
        { appointmentId: selectedAppointment.id },
        { confirmed: willAttend }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || (willAttend ? 'Appointment confirmed!' : 'Appointment cancelled'));
        
        // Mark notification as read after action is taken
        if (selectedNotificationId) {
          await markAsRead(selectedNotificationId);
        }
        
        setConfirmDialogOpen(false);
        setSelectedAppointment(null);
        setSelectedNotificationId(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update appointment');
      }
    } catch (error) {
      console.error('Error confirming appointment:', error);
      toast.error('Failed to update appointment');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleStaffAppointmentClose = async () => {
    // Mark notification as read when staff closes the dialog
    if (selectedNotificationId) {
      await markAsRead(selectedNotificationId);
    }
    
    setStaffAppointmentDialogOpen(false);
    setSelectedAppointment(null);
    setSelectedNotificationId(null);
  };

  const getNotificationIcon = (notification: any) => {
    // Check for Job Board notifications
    if (notification.title?.includes('Job Board') || notification.metadata?.type === 'job_board') {
      return <Briefcase className="h-5 w-5 text-purple-600" />;
    }
    
    // Check for Invitation notifications
    if (notification.title?.includes('Invitation') || notification.metadata?.invitationId) {
      return <Mail className="h-5 w-5 text-orange-600" />;
    }
    
    // Default icons for other types
    switch (notification.type) {
      case 'appointment':
        return '📅';
      case 'timesheet':
        return '⏰';
      case 'leave':
        return '🌴';
      case 'system':
        return '⚙️';
      default:
        return '📢';
    }
  };

  const getNotificationColors = (notification: any) => {
    // Check for Job Board notifications
    if (notification.title?.includes('Job Board') || notification.metadata?.type === 'job_board') {
      return {
        bg: 'bg-purple-50 dark:bg-purple-950',
        border: 'border-purple-200',
        dot: 'bg-purple-600'
      };
    }
    
    // Check for Invitation notifications
    if (notification.title?.includes('Invitation') || notification.metadata?.invitationId) {
      return {
        bg: 'bg-orange-50 dark:bg-orange-950',
        border: 'border-orange-200',
        dot: 'bg-orange-600'
      };
    }
    
    // Default blue for other notifications
    return {
      bg: 'bg-blue-50 dark:bg-blue-950',
      border: 'border-blue-200',
      dot: 'bg-blue-600'
    };
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative text-white hover:bg-[#4A4C7D]">
            <Bell className="h-6 w-6" />
            {totalUnreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-6 w-6 rounded-full p-0 flex items-center justify-center text-sm font-bold"
              >
                {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="flex items-center justify-between p-4 pb-2">
            <h3 className="font-semibold text-lg">Notifications</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAllNotificationsModalOpen(true);
                setOpen(false);
              }}
              className="text-xs text-primary hover:text-primary/80"
            >
              View All
            </Button>
          </div>
          
          <Separator />
          
          <ScrollArea className="h-[400px]">
            {isLoading && activeShiftsLoading ? (
              <div className="p-4 text-center text-muted-foreground">
                Loading notifications...
              </div>
            ) : (
              <div className="divide-y">
                {/* Unread Messages - Summary Notification */}
                {unreadMessagesCount > 0 && (
                  <div
                    className="p-4 cursor-pointer hover:bg-accent transition-colors bg-blue-50 dark:bg-blue-950"
                    onClick={() => handleNotificationClick({ source: 'chat' })}
                  >
                    <div className="flex gap-3">
                      <div className="text-2xl flex-shrink-0">
                        <MessageSquare className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-sm text-foreground">
                            Unread Messages
                          </p>
                          <div className="h-2 w-2 rounded-full bg-blue-600 flex-shrink-0 mt-1" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          You have {unreadMessagesCount} unread conversation{unreadMessagesCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Active Shifts - Summary Notification */}
                {activeShiftsCount > 0 && (
                  <div
                    className="p-4 cursor-pointer hover:bg-accent transition-colors bg-blue-50 dark:bg-blue-950"
                    onClick={() => handleNotificationClick({ source: 'active-shift' })}
                  >
                    <div className="flex gap-3">
                      <div className="text-2xl flex-shrink-0">
                        <Clock className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-sm text-foreground">
                            Active Shifts
                          </p>
                          <div className="h-2 w-2 rounded-full bg-green-600 flex-shrink-0 mt-1" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          You have {activeShiftsCount} active shift{activeShiftsCount !== 1 ? 's' : ''} to clock out
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Job Board and Invitations now come from Firestore notifications */}

                {/* Firestore Notifications - Only show unread */}
                {notifications.filter(n => !n.read).map((notification) => {
                  const colors = getNotificationColors(notification);
                  return (
                    <div
                      key={notification.id}
                      className={`p-4 cursor-pointer hover:bg-accent transition-colors ${colors.bg}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex gap-3">
                        <div className="text-2xl flex-shrink-0">
                          {getNotificationIcon(notification)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`font-medium text-sm ${
                              !notification.read ? 'text-foreground' : 'text-muted-foreground'
                            }`}>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <div className={`h-2 w-2 rounded-full ${colors.dot} flex-shrink-0 mt-1`} />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Show empty state only if truly no unread notifications */}
                {notifications.filter(n => !n.read).length === 0 && activeShiftsCount === 0 && unreadMessagesCount === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No unread notifications</p>
                    <p className="text-xs mt-2">Click "View All" to see older notifications</p>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Appointment Confirmation Dialog (for clients) */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Your Appointment</DialogTitle>
            <DialogDescription>
              Your appointment is coming up in 48 hours. Please confirm whether you'll be attending.
            </DialogDescription>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-3 py-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Service</p>
                <p className="text-base">{selectedAppointment.serviceName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Time</p>
                <p className="text-base">
                  {formatTimeFromMetadata(selectedAppointment.startTime)} - {formatTimeFromMetadata(selectedAppointment.endTime)}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex-row gap-2">
            <Button
              onClick={() => handleConfirm(true)}
              disabled={isConfirming}
              className="bg-green-600 hover:bg-green-700 text-white flex-1 h-12"
            >
              {isConfirming ? 'Confirming...' : 'Yes, I\'m Coming'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleConfirm(false)}
              disabled={isConfirming}
              className="flex-1 h-12"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Appointment Details Dialog */}
      <Dialog open={staffAppointmentDialogOpen} onOpenChange={setStaffAppointmentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
            <DialogDescription>
              Appointment has been updated. Please check the details:
            </DialogDescription>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Service</p>
                <p className="text-base font-medium">{selectedAppointment.serviceName || 'N/A'}</p>
              </div>
              
              {selectedAppointment.clientName && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Client</p>
                  <p className="text-base font-medium">{selectedAppointment.clientName}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Date & Time</p>
                <p className="text-base">
                  {selectedAppointment.date || formatTimeFromMetadata(selectedAppointment.startTime)}
                </p>
                <p className="text-base text-muted-foreground">
                  {selectedAppointment.time || `${formatTimeFromMetadata(selectedAppointment.startTime)} - ${formatTimeFromMetadata(selectedAppointment.endTime)}`}
                </p>
              </div>

              {selectedAppointment.location && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Location</p>
                  <p className="text-base">{selectedAppointment.location}</p>
                </div>
              )}

              <div className="pt-2">
                <Button
                  onClick={() => {
                    handleStaffAppointmentClose();
                    navigate(`/appointment-details-page?id=${selectedAppointment.id}`);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  View Appointment Details
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={handleStaffAppointmentClose}
              className="w-full"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Notifications Modal */}
      <Dialog open={allNotificationsModalOpen} onOpenChange={setAllNotificationsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>All Notifications</DialogTitle>
            <DialogDescription>
              Showing notifications from the last 7 days and all unread notifications
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-2">
              {/* Chat Messages */}
              {unreadMessagesCount > 0 && (
                <div
                  className="p-4 rounded-lg border cursor-pointer hover:bg-accent transition-colors bg-blue-50 dark:bg-blue-950 border-blue-200"
                  onClick={() => {
                    setAllNotificationsModalOpen(false);
                    handleNotificationClick({ source: 'chat' });
                  }}
                >
                  <div className="flex gap-3">
                    <div className="text-2xl flex-shrink-0">
                      <MessageSquare className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm text-foreground">
                          Unread Messages
                        </p>
                        <Badge variant="default" className="text-xs">New</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        You have {unreadMessagesCount} unread conversation{unreadMessagesCount !== 1 ? 's' : ''}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">Just now</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Shifts */}
              {activeShiftsCount > 0 && (
                <div
                  className="p-4 rounded-lg border cursor-pointer hover:bg-accent transition-colors bg-blue-50 dark:bg-blue-950 border-blue-200"
                  onClick={() => {
                    setAllNotificationsModalOpen(false);
                    handleNotificationClick({ source: 'active-shift' });
                  }}
                >
                  <div className="flex gap-3">
                    <div className="text-2xl flex-shrink-0">
                      <Clock className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm text-foreground">
                          Active Shifts
                        </p>
                        <Badge variant="default" className="text-xs">New</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        You have {activeShiftsCount} active shift{activeShiftsCount !== 1 ? 's' : ''}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">Just now</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Job Board and Invitations now come from Firestore notifications */}

              {/* Firestore Notifications - Filter to last 7 days + unread */}
              {(() => {
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                
                const filteredNotifications = notifications.filter((notification) => {
                  // Always show unread notifications
                  if (!notification.read) return true;
                  
                  // For read notifications, only show if within last 7 days
                  const notificationDate = notification.createdAt.toDate();
                  return notificationDate >= sevenDaysAgo;
                });
                
                return filteredNotifications.length > 0 ? (
                  filteredNotifications.map((notification) => {
                    const colors = getNotificationColors(notification);
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 rounded-lg border cursor-pointer hover:bg-accent transition-colors ${
                          !notification.read ? `${colors.bg} ${colors.border}` : 'bg-background'
                        }`}
                        onClick={() => {
                          setAllNotificationsModalOpen(false);
                          handleNotificationClick(notification);
                        }}
                      >
                        <div className="flex gap-3">
                          <div className="text-2xl flex-shrink-0">
                            {getNotificationIcon(notification)}
                          </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-medium text-sm ${
                            !notification.read ? 'text-foreground' : 'text-muted-foreground'
                          }`}>
                            {notification.title}
                          </p>
                          <div className="flex items-center gap-2">
                            {!notification.read && (
                              <Badge variant="default" className="text-xs">New</Badge>
                            )}
                            {notification.read && (
                              <Badge variant="outline" className="text-xs">Read</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {notification.message}
                        </p>
                        <div className="flex flex-col gap-1 mt-2">
                          <p className="text-xs text-muted-foreground">
                            {notification.createdAt.toDate().toLocaleString('en-AU', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            ({formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true })})
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                    );
                  })
                ) : (
                  filteredNotifications.length === 0 && 
                  unreadMessagesCount === 0 && 
                  activeShiftsCount === 0 && (
                    <div className="p-8 text-center text-muted-foreground">
                      <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No recent notifications</p>
                      <p className="text-xs mt-2">Showing last 7 days + unread</p>
                    </div>
                  )
                );
              })()}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAllNotificationsModalOpen(false)}
            >
              Close
            </Button>
            {notifications.some(n => !n.read) && (
              <Button
                onClick={async () => {
                  if (user?.uid) await markAllAsRead(user.uid);
                  toast.success('All notifications marked as read');
                }}
              >
                Mark All as Read
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
